// Phase 84.M.82 — Global modal nhắc xác thực điểm trình cho user chưa verified.
//
// User choices: 1a (popup khi A+B, không pop C+D) / 2a (all athlete routes) /
//               3a (LocalStorage 8h từ lần dismiss gần nhất) / 5a (reuse
//               SelfRatingVerificationModal).
//
// Behavior:
// - Trigger khi:
//   • User đã login.
//   • Path bắt đầu bằng `/athlete/` (không pop ở admin / login / public).
//   • `rating_verified` falsy.
//   • KHÔNG có pending request (status=pending) trong `/api/athlete/rating-verification/me`.
//   • LocalStorage `pl_rating_verify_dismissed_at` đã quá 8h (hoặc chưa có).
// - 1 nút "Đồng ý" → đóng popup + mở `SelfRatingVerificationModal` ngay lập tức.
// - Nút "Để sau" (secondary nhỏ) → set LS now → ẩn 8h tiếp.
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Warning, ArrowRight } from "@phosphor-icons/react";
import { Modal } from "../components/DataTable";
import SelfRatingVerificationModal from "../components/athlete/personal/SelfRatingVerificationModalSmart";
import RatingVerifyIllustration from "../components/athlete/personal/RatingVerifyIllustration";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";

const DISMISS_KEY = "pl_rating_verify_dismissed_at";
const RECHECK_AFTER_HOURS = 8;

function shouldShowByDismiss() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return true;
    const last = Number(raw);
    if (!Number.isFinite(last)) return true;
    const diffMs = Date.now() - last;
    return diffMs >= RECHECK_AFTER_HOURS * 3600 * 1000;
  } catch {
    return true;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export default function RatingVerifyReminderGate() {
  const { user } = useAuth();
  const location = useLocation();
  const [reminderOpen, setReminderOpen] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [pendingChecked, setPendingChecked] = useState(false);
  // v16.61.4 — Bỏ fetch `mode` (OCR đã ẩn khỏi luồng user). Popup nhắc luôn
  // dùng text self_declare: "nhập điểm trình + chọn nền tảng, BTC duyệt
  // trong 24h". Nếu sau này bật lại OCR, đọc lại file RatingVerifyReminderGate
  // ở lịch sử git để lấy nhánh cũ.

  // Chỉ pop ở route /athlete/*.
  const onAthleteRoute = useMemo(
    () => location.pathname.startsWith("/athlete/"),
    [location.pathname],
  );

  // Reset pendingChecked khi user đổi → check lại lần kế tiếp.
  useEffect(() => {
    setPendingChecked(false);
  }, [user?.id]);

  useEffect(() => {
    // Guards trước khi gọi API.
    if (!user?.id) return;
    if (!onAthleteRoute) return;
    if (user.rating_verified) return; // case D
    if (pendingChecked) return;
    if (!shouldShowByDismiss()) {
      setPendingChecked(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/athlete/rating-verification/me", {
          params: { status: "pending", page_size: 1 },
        });
        const hasPending = Array.isArray(r.data?.items)
          ? r.data.items.length > 0
          : Array.isArray(r.data)
          ? r.data.length > 0
          : false;
        if (cancelled) return;
        // Case C — đã submit pending → KHÔNG pop.
        if (hasPending) {
          setPendingChecked(true);
          return;
        }
        // Case A + B → pop.
        setReminderOpen(true);
        setPendingChecked(true);
      } catch {
        // Nếu API lỗi → vẫn pop (an toàn — không silent miss reminder).
        if (cancelled) return;
        setReminderOpen(true);
        setPendingChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.rating_verified, onAthleteRoute, pendingChecked]);

  const onAccept = () => {
    markDismissed();
    setReminderOpen(false);
    // Mở modal Tự duyệt điểm trình ngay.
    setVerifyModalOpen(true);
  };

  const onSnooze = () => {
    markDismissed();
    setReminderOpen(false);
  };

  // Phase 84.M.84 — Detect case A (chưa có self-claim — rating còn = default
  // theo gender từ signup) vs case B (đã có self-claim rating khác default
  // nhưng chưa verified). Hiển thị 2 wording khác nhau (user choice 5b).
  const isCaseA = (() => {
    if (!user?.rating) return true;
    const r = Number(user.rating);
    if (!Number.isFinite(r)) return true;
    const g = (user.gender || "").toLowerCase();
    if (g === "male" && Math.abs(r - 2.55) < 0.001) return true;
    if (g === "female" && Math.abs(r - 2.05) < 0.001) return true;
    if (g === "other" && Math.abs(r - 2.05) < 0.001) return true;
    return false;
  })();

  // Modal nhắc — chỉ render khi reminderOpen + user còn unverified.
  return (
    <>
      <Modal
        open={reminderOpen}
        onClose={onSnooze}
        title={null}
        testid="rating-verify-reminder-modal"
      >
        <div className="space-y-4">
          {/* Phase 84.M.102 — Hero illustration giúp user hiểu loại ảnh cần upload. */}
          <RatingVerifyIllustration testId="rating-reminder-illustration" maxHeight={180} />
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Warning size={22} weight="bold" className="text-amber-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-black text-white leading-tight">
                Bạn cần cập nhật điểm trình
              </div>
              {isCaseA ? (
                <p
                  className="text-[14px] text-white/85 leading-relaxed mt-2 font-medium"
                  data-testid="rating-verify-reminder-text-case-a"
                >
                  Chỉ cần <b className="text-[#A8FF00]">nhập điểm trình hiện tại</b> và
                  chọn nền tảng (Baseline / PVNA / DUPR), BTC sẽ duyệt
                  trong 24h. Sau khi được duyệt, bạn có thể đăng ký các giải có
                  kiểm tra trình độ.
                </p>
              ) : (
                <p
                  className="text-[14px] text-white/85 leading-relaxed mt-2 font-medium"
                  data-testid="rating-verify-reminder-text-case-b"
                >
                  Bạn đã nhập điểm trình tự đánh giá nhưng chưa được BTC
                  xác thực. Hãy <b className="text-[#A8FF00]">khai chính thức</b> điểm
                  trình hiện tại + chọn nền tảng nguồn để BTC duyệt
                  (chỉ mất 30 giây).
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <button
              type="button"
              onClick={onAccept}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:from-[#FF9F1C] hover:to-[#FFB145] text-white text-[13.5px] font-bold transition shadow-lg shadow-orange-500/20"
              data-testid="rating-verify-reminder-accept"
            >
              Đồng ý <ArrowRight size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={onSnooze}
              className="text-[11.5px] text-white/45 hover:text-white/70 transition py-1"
              data-testid="rating-verify-reminder-snooze"
            >
              Để sau (8 giờ nhắc lại)
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Tự duyệt điểm trình — reuse component sẵn có ở Personal (user choice 5a). */}
      <SelfRatingVerificationModal
        open={verifyModalOpen}
        onClose={() => setVerifyModalOpen(false)}
        onSubmitted={() => {
          setVerifyModalOpen(false);
          // Sau submit thành công → mark dismissed luôn để không pop ngay
          // lại trong phiên (user vẫn pending, sẽ check lại đợt sau).
          markDismissed();
        }}
      />
    </>
  );
}

// Export helper cho tests dễ stub.
export const __dismissKey = DISMISS_KEY;
export const __recheckHours = RECHECK_AFTER_HOURS;
